import { FormEvent, useState } from "react";
import { Navigate } from "react-router-dom";

import { getAuthToken } from "../lib/supabase";
import { useUser } from "../lib/UserContext";

type SendStatus =
  | { type: "success"; message: string }
  | { type: "error"; message: string }
  | null;

const DEFAULT_HTML = `<!doctype html>
<html>
  <body style="margin:0;background:#f5f7fb;font-family:Arial,sans-serif;color:#0f172a;">
    <div style="max-width:640px;margin:0 auto;padding:32px 20px;">
      <div style="background:#ffffff;border-radius:20px;padding:32px;border:1px solid #e2e8f0;">
        <p style="margin:0 0 12px;font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:#0f766e;">
          ConnectLah
        </p>
        <h1 style="margin:0 0 16px;font-size:28px;line-height:1.2;">
          Your subject here
        </h1>
        <p style="margin:0 0 16px;font-size:16px;line-height:1.7;color:#334155;">
          Write your email content in HTML. The live preview updates as you edit.
        </p>
        <p style="margin:0;font-size:16px;line-height:1.7;color:#334155;">
          You can include links, brand colors, buttons, and external images.
        </p>
      </div>
    </div>
  </body>
</html>`;

const INPUT_CLASS =
  "w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-800 shadow-sm transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-300";

function isValidEmail(value: string) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
}

function hasHtmlMarkup(value: string) {
  return /<\/?[a-z][\w:-]*(?:\s[^>]*)?>/i.test(value);
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function buildPreviewHtml(value: string) {
  const normalized = value.trim().replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!normalized) {
    return DEFAULT_HTML;
  }
  if (hasHtmlMarkup(normalized)) {
    return normalized;
  }

  const escaped = escapeHtml(normalized);
  return `<!doctype html>
<html>
  <body style="margin:0;padding:24px;background:#ffffff;font-family:Arial,sans-serif;color:#0f172a;">
    <div style="white-space:pre-wrap;line-height:1.7;">${escaped}</div>
  </body>
</html>`;
}

export default function AdminEmail() {
  const user = useUser();
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState<SendStatus>(null);

  if (user && user.role !== "admin") {
    return <Navigate to="/conversations" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus(null);

    const trimmedTo = to.trim();
    const trimmedSubject = subject.trim();
    const trimmedHtml = html.trim();

    if (!isValidEmail(trimmedTo)) {
      setStatus({ type: "error", message: "Please enter a valid recipient email." });
      return;
    }
    if (!trimmedSubject) {
      setStatus({ type: "error", message: "Subject is required." });
      return;
    }
    if (!trimmedHtml) {
      setStatus({ type: "error", message: "HTML content is required." });
      return;
    }

    setIsSending(true);

    try {
      const authHeader = await getAuthToken();
      if (!authHeader) {
        throw new Error("Missing authenticated session.");
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/email/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authHeader,
        },
        body: JSON.stringify({
          to: trimmedTo,
          subject: trimmedSubject,
          html: trimmedHtml,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(typeof data.detail === "string" ? data.detail : "Unable to send email.");
      }

      setStatus({
        type: "success",
        message:
          typeof data.id === "string" && data.id
            ? `Email sent. Resend id: ${data.id}`
            : "Email sent.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: error instanceof Error ? error.message : "Unable to send email.",
      });
    } finally {
      setIsSending(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Admin Email Sender</h1>
        <p className="mt-1 text-sm text-slate-500">
          Send custom HTML emails or paste plain text through Resend from{" "}
          <span className="font-medium">Connectlah &lt;contact@connectlah.app&gt;</span>.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.95fr)]">
        <form className="space-y-5 rounded-xl bg-white p-6 shadow" onSubmit={handleSubmit}>
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">From</label>
              <input
                className={`${INPUT_CLASS} bg-slate-50 text-slate-500`}
                disabled
                readOnly
                value="Connectlah <contact@connectlah.app>"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700" htmlFor="admin-email-to">
                To
              </label>
              <input
                id="admin-email-to"
                className={INPUT_CLASS}
                onChange={(event) => setTo(event.target.value)}
                placeholder="client@example.com"
                type="email"
                value={to}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-slate-700" htmlFor="admin-email-subject">
              Subject
            </label>
            <input
              id="admin-email-subject"
              className={INPUT_CLASS}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="Your email subject"
              value={subject}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <label className="block text-sm font-medium text-slate-700" htmlFor="admin-email-html">
                HTML
              </label>
              <span className="text-xs text-slate-400">
                Plain text is auto-converted and line breaks are preserved.
              </span>
            </div>
            <textarea
              id="admin-email-html"
              className={`${INPUT_CLASS} min-h-[460px] resize-y font-mono text-xs leading-6`}
              onChange={(event) => setHtml(event.target.value)}
              spellCheck={false}
              value={html}
            />
          </div>

          {status && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                status.type === "success"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              {status.message}
            </div>
          )}

          <div className="flex justify-end">
            <button
              className="rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSending}
              type="submit"
            >
              {isSending ? "Sending..." : "Send email"}
            </button>
          </div>
        </form>

        <section className="space-y-3 rounded-xl bg-white p-6 shadow">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Preview</h2>
            <p className="mt-1 text-sm text-slate-500">
              Rendered in an isolated iframe so your email styles do not affect the backoffice.
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
            <iframe
              className="h-[760px] w-full bg-white"
              sandbox=""
              srcDoc={buildPreviewHtml(html)}
              title="Email preview"
            />
          </div>
        </section>
      </div>
    </div>
  );
}
